import { FC, useState, useContext, lazy } from 'react';
import { Button } from '@cfa/react-core';
import styled from 'styled-components';
import {
  mediaQueries,
  themeColors,
  themeSizes,
} from '../../styles/globalStyleVars';
import { UserContext } from '../../user/UserContext';
import { UserContextType } from '../../@types/user';
import { Link } from 'react-router-dom';
import { IconMessage2Share, IconDeviceMobile } from '@tabler/icons-react';
import SWLogo from '../../assets/sw-logo.svg';
import { callEvent } from '../../utils/Amplitude/AmplitudeUtilities';
import WebsiteFeedbackModal from '../Global/Modals/WebsiteFeedbackModal/WebsiteFeedbackModal';
import { useLocationStore } from '../../stores/locationStore';
import { generateSupportAppLink } from '../../utils/linkHelper';
import { TypographyStyled } from '../Styled/Typography';
import useBreakpoints from '../../hooks/useBreakpoints';
import useMediaQuery from '../../hooks/useMediaQuery';

const DownloadAppAndScanModal = lazy(
  () => import('../Global/Modals/DownloadAndScanModal/DownloadAppAndScanModal')
);

const FooterWrapper = styled.div`
  background-color: ${themeColors.notificationBackgroundColor};
  margin-top: auto;
  margin-bottom: 0;
  width: 100%;
`;

const FooterContainer = styled.div`
  padding-top: ${themeSizes.lg};
  padding-bottom: ${themeSizes.lg};
  ${mediaQueries.sm_up} {
    padding-top: ${themeSizes.xl};
    padding-bottom: ${themeSizes.xl};
  }
`;

const LogoWithText = styled.span`
  display: flex;
  align-items: center;
  margin-bottom: ${themeSizes.sm};
  img {
    margin-right: ${themeSizes.sm};
  }
  &.support-app {
    padding-bottom: ${themeSizes.sm};
  }
`;

const IconWrapper = styled.span`
  font-size: ${themeSizes.lg};
  color: ${themeColors.colorGray_6};
  margin-right: ${themeSizes.md};
  display: flex;
`;

const FooterHeading = styled(TypographyStyled)`
  color: ${themeColors.colorGray_6};

  &.responsive-bottom-margin {
    margin-bottom: ${themeSizes.md};

    ${mediaQueries.sm_up} {
      margin-bottom: ${themeSizes.sm};
    }

    ${mediaQueries.md_up} {
      margin-bottom: ${themeSizes.md};
    }
  }
`;

const FooterText = styled(TypographyStyled)`
  &.underline {
    text-decoration: underline;
  }
  &.with-bottom-margin {
    margin-bottom: ${themeSizes.sm};
  }
`;

const OpenModalButton = styled(Button)<{ $isLastButton?: boolean }>`
  background-color: ${themeColors.primaryWhite} !important;
  margin-top: ${themeSizes.md};
  margin-bottom: ${({ $isLastButton }) =>
    $isLastButton ? '0' : `${themeSizes.lg}`};
  ${mediaQueries.sm_up} {
    margin-bottom: 0px;
  }
`;

const FooterBottomRow = styled.div`
  justify-content: space-between;
  align-items: center;
  padding-top: ${themeSizes.xl};
  ${mediaQueries.sm_up} {
    padding-top: 80px;
  }
`;

const FooterIcon = styled.img`
  &.chick-fil-a-support {
    width: ${themeSizes.lg};
    height: auto;
  }
  &.support-circle {
    width: ${themeSizes.xxl};
    height: auto;
  }
`;

const StyledPhoneLink = styled.a`
  color: ${themeColors.secondaryBlue};
  padding-left: 0px;
  text-decoration: none;
  font-weight: bold;
  text-decoration: underline;
`;

export const Footer: FC = () => {
  const { activeLocation } = useLocationStore();
  const breakpoints = useBreakpoints();
  const isExtraSmallScreen = useMediaQuery(breakpoints.down('sm'));
  //generates current year for footer
  const currentYear = new Date().getFullYear();
  const [showDownloadAppModal, setShowDownloadAppModal] =
    useState<boolean>(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const { userState } = useContext(UserContext) as UserContextType;

  const openDownloadApp = () => {
    callEvent('view download app', {
      deviceType: userState.mobileOS ?? 'Desktop/Laptop',
    });
    generateSupportAppLink(setShowDownloadAppModal, userState.mobileOS);
  };

  const FeebackCol: FC<{ isLastButton?: boolean }> = ({ isLastButton }) => (
    <div className='col-xl-4 col-md-4 col-sm-4 col-12'>
      <LogoWithText>
        <IconWrapper>
          <IconMessage2Share />
        </IconWrapper>
        <FooterHeading variant='h4'>
          We&apos;d love to hear what you think
        </FooterHeading>
      </LogoWithText>
      <TypographyStyled>
        What do you think of the new website?
        <br /> All feedback will help us better support you.
      </TypographyStyled>
      <OpenModalButton
        onPress={() => setShowFeedbackModal(true)}
        size='sm'
        variant='outlined'
        data-testid='footer-app-btn-feedback'
        $isLastButton={!!isLastButton}
        style={{ padding: '0 1.5em' }}
      >
        Submit feedback
      </OpenModalButton>
    </div>
  );

  return (
    <FooterWrapper data-testid='footer-wrapper'>
      <FooterContainer className='container'>
        <div className='row' style={{ justifyContent: 'space-between' }}>
          <div className='col-xl-4 col-md-4 col-sm-4 col-12'>
            <LogoWithText>
              <IconWrapper>
                <IconDeviceMobile />
              </IconWrapper>
              <FooterHeading variant='h4'>
                Download the CFA Support App
              </FooterHeading>
            </LogoWithText>
            <TypographyStyled>
              Get updates on your tickets, reply to and manage tickets, upload
              files and more from your phone.
            </TypographyStyled>
            <OpenModalButton
              onPress={openDownloadApp}
              size='sm'
              variant='outlined'
              data-testid='footer-app-btn-app'
            >
              Download app
            </OpenModalButton>
          </div>
          <FeebackCol />

          <div className='col-xl-3 col-md-3 col-sm-4 col-12'>
            <FooterHeading variant='h4' className='responsive-bottom-margin'>
              Contact Support Now (24/6)
            </FooterHeading>
            <FooterText>For critical issues:</FooterText>
            <FooterText className='with-bottom-margin'>
              Call&nbsp;
              <StyledPhoneLink
                href='tel:1-800-232-2677'
                target='_self'
                onClick={() =>
                  callEvent('call button on get help', {
                    locationNum: activeLocation,
                  })
                }
                data-testid='footer-phone-link'
              >
                1-800-232-2677
              </StyledPhoneLink>
            </FooterText>
            <FooterText style={{ marginBottom: themeSizes.sm }}>
              For non-critical issues:
            </FooterText>
            <Link
              to={'/forms'}
              style={{ margin: '0px', textDecoration: 'none' }}
            >
              <Button
                size='sm'
                data-testid='footer-open-ticket-btn'
                style={{ marginBottom: isExtraSmallScreen ? themeSizes.xl : 0 }}
              >
                Open a ticket
              </Button>
            </Link>
          </div>
        </div>
        <FooterBottomRow className='row'>
          <div className='col-xl-4 col-sm-6'>
            <LogoWithText>
              <FooterIcon
                src={SWLogo}
                className='chick-fil-a-support'
                alt='Chick-fil-A Support Logo'
              />
              <TypographyStyled
                text='Chick-fil-A Support'
                fontWeight='bold'
                color={themeColors.primaryRed}
              />
            </LogoWithText>
          </div>
          <div className='col-xl-3 col-md-4 col-sm-6'>
            <TypographyStyled
              text={`©${currentYear} Chick-fil-A, Inc. All rights reserved`}
              variant='caption1'
            />
          </div>
        </FooterBottomRow>
      </FooterContainer>
      <DownloadAppAndScanModal
        onClose={() => setShowDownloadAppModal(false)}
        show={showDownloadAppModal}
        title='Download the CFA Support App'
        subTitle='Scan with your smartphone camera to download.'
        modalType='download'
        deviceType={userState.mobileOS}
      />
      <WebsiteFeedbackModal
        show={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        title={'Tell us your thoughts about the new website'}
        subTitle={
          <>
            Please tell us what&apos;s on your mind. The more detail you share
            the better we can serve you.&nbsp;
            <b>We&apos;ll create a ticket to submit your feedback.</b>
          </>
        }
        screenName='Footer'
        userGUID={userState.userDetails?.['cfa-guid']}
      />
    </FooterWrapper>
  );
};
